import React from 'react';
import classnames from 'classnames';

import lActive from 'public/static/images/pagination/L_Active.svg';
import rActive from 'public/static/images/pagination/R_Active.svg';
import lHover from 'public/static/images/pagination/L_Hover.svg';
import rHover from 'public/static/images/pagination/R_Hover.svg';
import lDisabled from 'public/static/images/pagination/L_Disabled.svg';
import rDisabled from 'public/static/images/pagination/R_Disabled.svg';

import { colors } from 'components/styles/variables';
import { getNavItems, PAGE_GAP } from './getNavItems';

export interface PaginationProps {
  onPageChange: (page: number) => void;
  currentPage: number;
  pageSize: number;
  totalResults: number;
}

export default function Pagination(props: PaginationProps): JSX.Element | null {
  const navItems = getNavItems({
    currentPage: props.currentPage,
    pageSize: props.pageSize,
    totalResults: props.totalResults,
  });

  function handleClickLeftStepper(): void {
    if (navItems.leftStepper && props.currentPage) {
      props.onPageChange(props.currentPage - 1);
    }
  }

  function handleClickRightStepper(): void {
    if (navItems.rightStepper && props.currentPage) {
      props.onPageChange(props.currentPage + 1);
    }
  }

  function handleClickPageNumber(pageNumber: number): void {
    props.onPageChange(pageNumber);
  }

  if (!navItems.pageNumbers.length) {
    return null;
  }

  return (
    <div className="pagination">
      <button
        onClick={handleClickLeftStepper}
        className={classnames(
          'page-stepper-arrow page-stepper-arrow-left',
          navItems.leftStepper
            ? 'page-stepper-arrow-left-enabled'
            : 'page-stepper-arrow-left-disabled'
        )}
        aria-label="previous page"
        data-testid="page-stepper-left"
      />

      {navItems.pageNumbers.map((pageNumber, i) => {
        if (pageNumber === PAGE_GAP) {
          return (
            <span key={`${pageNumber.toString()}-${i}`} className="page-number-gap">
              ...
            </span>
          );
        }

        return (
          <button
            key={pageNumber.toString()}
            className={classnames('page-number', {
              'page-number-current': props.currentPage === pageNumber,
            })}
            onClick={(): void => handleClickPageNumber(pageNumber)}
          >
            {pageNumber}
          </button>
        );
      })}

      <button
        onClick={handleClickRightStepper}
        className={classnames(
          'page-stepper-arrow page-stepper-arrow-right',
          navItems.rightStepper
            ? 'page-stepper-arrow-right-enabled'
            : 'page-stepper-arrow-right-disabled'
        )}
        aria-label="next page"
        data-testid="page-stepper-right"
      />

      <style jsx>{`
        .pagination {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .page-number,
        .page-number-gap {
          all: unset;
          display: flex;
          justify-content: center;
          align-items: center;
          min-width: 28px;
          height: 28px;
          text-align: center;
          font-size: 16px;
          font-weight: 600;
          color: ${colors.BLACK};
          border-bottom: solid 2px transparent;
        }

        .page-number {
          cursor: pointer;
        }

        .page-number-gap {
          color: ${colors.G3};
        }

        .page-number-current,
        .page-number:hover {
          color: ${colors.ALR1};
          border-bottom: solid 2px ${colors.ALR1};
        }

        .page-stepper-arrow {
          all: unset;
          width: 34px;
          height: 34px;
        }

        .page-stepper-arrow-left {
          margin-right: 16px;
        }

        .page-stepper-arrow-left-enabled {
          background-image: url('${lActive}');
          cursor: pointer;
        }

        .page-stepper-arrow-left-enabled:hover {
          background-image: url('${lHover}');
        }

        .page-stepper-arrow-left-disabled {
          background-image: url('${lDisabled}');
        }

        .page-stepper-arrow-right {
          margin-left: 16px;
        }

        .page-stepper-arrow-right-enabled {
          background-image: url('${rActive}');
          cursor: pointer;
        }

        .page-stepper-arrow-right-enabled:hover {
          background-image: url('${rHover}');
        }

        .page-stepper-arrow-right-disabled {
          background-image: url('${rDisabled}');
        }
      `}</style>
    </div>
  );
}
